import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { TaskCapture } from "../../types";

/**
 * Captures whose delete is waiting out the undo window. A reload during that
 * window still returns them from the database. {@link visibleCaptures} hides
 * them so one reload cannot put a half-deleted capture back on the list.
 */
const pendingCaptureDeleteIds = new Set<string>();

export function visibleCaptures(rows: TaskCapture[]): TaskCapture[] {
  if (pendingCaptureDeleteIds.size === 0) return rows;
  return rows.filter((row) => !pendingCaptureDeleteIds.has(row.id));
}
import {
  showUndoToast,
  registerPendingWrite,
  clearPendingWrite,
  UNDO_WINDOW_MS,
} from "../../lib/undoStack";
import {
  getCurrentAccessToken,
  keepaliveSupabaseRequest,
} from "../../lib/keepaliveRest";

export function useDismissCapture() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from("task_captures").delete().eq("id", id);
      if (error) throw error;
    },
    // Optimistic removal so the list updates instantly.
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.taskCaptures });
      const previous = queryClient.getQueryData<TaskCapture[]>(
        queryKeys.taskCaptures
      );
      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useTaskCaptures out of its pending state, and neither the snapshot
      // (also undefined) nor onError's guard could put it back. The snapshot is
      // deliberately not defaulted to `[]` for the same reason — rolling back
      // to `[]` would re-introduce exactly the value we avoided writing.
      queryClient.setQueryData<TaskCapture[]>(queryKeys.taskCaptures, (prev) =>
        prev ? prev.filter((c) => c.id !== id) : prev
      );
      return { previous };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(queryKeys.taskCaptures, ctx.previous);
      }
      Sentry.captureException(err, { extra: { mutation: "useDismissCapture" } });
      toast.error("Couldn't remove that. Please try again.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.taskCaptures });
    },
  });
}

function putCaptureBack(queryClient: QueryClient, capture: TaskCapture) {
  queryClient.setQueryData<TaskCapture[]>(queryKeys.taskCaptures, (prev) => {
    const list = prev ?? [];
    if (list.some((c) => c.id === capture.id)) return list;
    return [...list, capture].sort((a, b) => {
      const byTime = a.created_at.localeCompare(b.created_at);
      return byTime !== 0 ? byTime : a.id.localeCompare(b.id);
    });
  });
}

/**
 * Review's Discard capture. The capture leaves the list at once, and a toast
 * offers Undo for the same window as other removals. The row is deleted only
 * after that window. Approving a capture and the list trash button stay on
 * {@link useDismissCapture}, which deletes immediately and does not toast.
 */
export function useDismissCaptureWithUndo() {
  const queryClient = useQueryClient();

  return async (
    capture: TaskCapture,
    opts?: { onFailed?: () => void },
  ): Promise<void> => {
    pendingCaptureDeleteIds.add(capture.id);
    let scheduled = false;
    try {
    await queryClient.cancelQueries({ queryKey: queryKeys.taskCaptures });
    const previous = queryClient.getQueryData<TaskCapture[]>(
      queryKeys.taskCaptures,
    );
    const listed = previous?.some((c) => c.id === capture.id) ?? false;
    if (listed) {
      queryClient.setQueryData<TaskCapture[]>(queryKeys.taskCaptures, (prev) =>
        prev ? prev.filter((c) => c.id !== capture.id) : prev,
      );
    }

    const accessToken = await getCurrentAccessToken();
    const pendingKey = `capture-dismiss-${capture.id}-${Date.now()}`;
    let undone = false;

    const restore = () => {
      pendingCaptureDeleteIds.delete(capture.id);
      if (!listed) return;
      putCaptureBack(queryClient, capture);
    };

    const deleteTimer = setTimeout(async () => {
      clearPendingWrite(pendingKey);
      if (undone) return;

      const { error } = await supabase
        .from("task_captures")
        .delete()
        .eq("id", capture.id);

      if (error) {
        restore();
        Sentry.captureException(error, {
          extra: {
            mutation: "useDismissCaptureWithUndo",
            captureId: capture.id,
          },
        });
        toast.error("Couldn't remove that. Please try again.");
        opts?.onFailed?.();
        return;
      }

      pendingCaptureDeleteIds.delete(capture.id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.taskCaptures });
    }, UNDO_WINDOW_MS);

    if (accessToken) {
      registerPendingWrite(pendingKey, {
        timer: deleteTimer,
        flush: () => {
          keepaliveSupabaseRequest(
            `task_captures?id=eq.${encodeURIComponent(capture.id)}`,
            { method: "DELETE" },
            accessToken,
          );
        },
      });
    }

    showUndoToast({
      label: "Capture discarded",
      onUndo: () => {
        undone = true;
        clearTimeout(deleteTimer);
        clearPendingWrite(pendingKey);
        restore();
      },
    });
    scheduled = true;
    } finally {
      if (!scheduled) pendingCaptureDeleteIds.delete(capture.id);
    }
  };
}
